/* eslint-disable react/prop-types */
import React from 'react';
import { STAGE_CONFIG, STAGE_ORDER } from '../sleep-summary/charts/SleepStageBreakdown';
import '../sleep-summary/SleepSummary.css';

/**
 * Per-session popup behind the "Sessions: N" cell of the Sleep tab's by-day table.
 *
 * One card per distinct `uuid` — that is what the N counts — showing when the
 * session ran, how long it was, where it came from, and its stage split. Data is
 * fetched by the caller (SleepTab) from /admin/api/user-sleep-sessions-by-date.
 */

const dmy = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(s);
};

const dur = (mins) => {
  const m = Number(mins);
  if (!Number.isFinite(m) || m <= 0) return '—';
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return h > 0 ? `${h}h ${rem}m` : `${rem}m`;
};

// Sleep epochs are stored in UTC; render them in UTC so the popup agrees with the
// day bucket the API grouped on (which is also derived from the same epoch).
const clock = (epochSeconds) => {
  const n = Number(epochSeconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n * 1000);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getUTCHours();
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${mm} ${ampm}`;
};

const timeWindow = (from, to) => {
  const a = clock(from);
  const b = clock(to);
  if (a && b) return `${a} – ${b} UTC`;
  return a || b || 'Time not recorded';
};

const orderStages = (stages = []) => [...stages].sort((a, b) => {
  const ai = STAGE_ORDER.indexOf(a.data_type);
  const bi = STAGE_ORDER.indexOf(b.data_type);
  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
});

const SessionCard = ({ session, index }) => {
  const stages = orderStages(session.stages);
  const reported = session.reported_total_minutes;
  const stageTotal = Number(session.stage_total_minutes) || 0;
  // The device-reported night total and the summed stages can disagree (missing or
  // overlapping stage rows) — say so instead of silently picking one.
  const mismatch = reported != null && stageTotal > 0 && Math.abs(Number(reported) - stageTotal) >= 1;

  return (
    <div className="ud-sess" data-testid={`ud-sleep-session-${index}`}>
      <div className="ud-sess__head">
        <span className="ud-sess__no">#{index + 1}</span>
        <div className="ud-sess__id">
          <div className="ud-sess__dur">{dur(session.duration_minutes)}</div>
          <div className="ud-sess__when">{timeWindow(session.time_from, session.time_to)}</div>
        </div>
        <div className="ud-sess__src">
          {session.platform ? <span className="ud-sess__tag">{session.platform}</span> : null}
          <div className="ud-sess__meta">{session.source_name || 'Unknown source'}</div>
          {session.device_id ? <div className="ud-sess__meta">{session.device_id}</div> : null}
        </div>
      </div>

      {mismatch ? (
        <div className="ud-sess__warn">
          Reported {dur(reported)}, stages total {dur(stageTotal)}
        </div>
      ) : null}

      {stages.length ? (
        <div className="ss-stage-list ud-sess__stages">
          {stages.map((stage) => {
            const cfg = STAGE_CONFIG[stage.data_type] || { label: stage.data_type, color: '#94a3b8' };
            return (
              <div className="ss-stage-row" key={stage.data_type}>
                <span className="ss-stage-pct" style={{ background: cfg.color }}>{Math.round(stage.percentage)}%</span>
                <span className="ss-stage-track">
                  <span style={{ width: `${Math.max(stage.percentage, 2)}%`, background: cfg.color }} />
                </span>
                <span className="ss-stage-name">{cfg.label} {dur(stage.total_minutes)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ud-sess__nostages">Total only — this session synced no stage rows.</div>
      )}

      {session.synced_at ? <div className="ud-sess__synced">Synced {session.synced_at} · {session.uuid}</div> : null}
    </div>
  );
};

const SleepSessionsModal = ({ open, date, sessions = [], loading, error, onClose }) => {
  if (!open) return null;
  const count = sessions.length;

  return (
    <div className="au-modal-bg" data-testid="ud-sleep-sessions-backdrop" onClick={onClose}>
      <div
        className="au-modal au-modal--wide"
        data-testid="ud-sleep-sessions-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="au-modal-close" onClick={onClose} aria-label="Close" title="Close">×</button>
        <h2 className="au-modal-title">Sleep sessions — {dmy(date)}</h2>

        {loading ? (
          <div className="au-modal-empty">Loading…</div>
        ) : error ? (
          <div className="au-modal-empty">We couldn’t load the sessions for this day.</div>
        ) : count === 0 ? (
          <div className="au-modal-empty">No session detail for this day.</div>
        ) : (
          <>
            <p className="ud-sess__count">{count} session{count === 1 ? '' : 's'} on this date</p>
            <div className="ud-sess-list">
              {sessions.map((s, i) => <SessionCard key={s.uuid || i} session={s} index={i} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SleepSessionsModal;
